import { useEffect, useState } from 'react'
import type { RefObject } from 'react'
import type { MapCanvasHandle } from './MapCanvas'
import { cabinetApi } from '../api'
import { isobathZoomStep } from './isobaths'
import { effectiveIsobathStep, ISOBATH_TOOL_KEY } from './isobathStep'
import { toolPending } from './toolPending'

/**
 * Слой раздела «Изобаты»: линии равных глубин с выбранным шагом поверх любого
 * слоя. Общий хук для «Карт» и «Аналитики» — раздел живёт в общем сайдбаре.
 *
 * Свой ключ слоя (`tool`), отдельный от апвеллинга: при одновременном включении
 * разделы затирали бы линии друг друга.
 *
 * Запрос дебаунсится: рельеф статичен, но пан/зум сыплют moveend, а каждый
 * ответ — сотни килобайт геометрии.
 */
const DEBOUNCE_MS = 250

export function useIsobathTool(
  mapRef: RefObject<MapCanvasHandle | null>,
  enabled: boolean,
  step: number,
  viewKey: string,
): { drawn: boolean; pending: boolean } {
  // Потолок инструмента — 6000 м (до 11.09.2026 было 1000, и над абиссалью
  // слой пустел: прод 2026-08-09, самая мелкая точка кадра — 1460 м). Кадр без
  // линий по-прежнему бывает — вне покрытия таблицы, — и легенда должна это
  // сказать, а не показывать зелёный образец над пустой картой.
  const [drawn, setDrawn] = useState(true)
  // Под какой запрос лежат линии. Нужен серии: кадр со включёнными изобатами
  // нельзя снимать, пока на карте лежат линии прошлого шага (см. toolPending).
  const [loadedKey, setLoadedKey] = useState<string | null>(null)

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!enabled) {
      map.removeIsobaths(ISOBATH_TOOL_KEY)
      setDrawn(true)
      setLoadedKey(null)
      return
    }
    let alive = true
    const id = setTimeout(() => {
      // Зум берём у самой карты: от него зависит упрощение геометрии на
      // бэкенде, и запрошенный «на глазок» дал бы либо кашу, либо мегабайты.
      const raw = map.getMap()?.getZoom() ?? 6
      const zoom = isobathZoomStep(raw)
      // Шаг мельче, чем позволяет зум, просить незачем: на весь Дальний Восток
      // шаг 10 м это 3,34 млн вершин против 229 тыс. у 200 м (замер прода
      // 12.09.2026), а упрощение всё равно сольёт их в гребёнку. Сервер
      // огрубляет и сам — здесь мы лишь не заказываем заведомо лишнего.
      const actual = effectiveIsobathStep(step, raw)
      cabinetApi
        .isobaths({ bbox: map.getBBox(), zoom, step: actual })
        .then(fc => {
          if (!alive) return
          setLoadedKey(`${step}|${viewKey}`)
          setDrawn(Boolean(fc.features?.length))
          if (!fc.features?.length) {
            // Вне покрытия таблицы (или глубже 6000 м) линий нет — это не сбой,
            // и старые линии чужого района висеть не должны.
            map.removeIsobaths(ISOBATH_TOOL_KEY)
            return
          }
          map.setIsobaths(ISOBATH_TOOL_KEY, fc as GeoJSON.FeatureCollection)
        })
        .catch(() => {
          if (!alive) return
          setLoadedKey(`${step}|${viewKey}`)
          map.removeIsobaths(ISOBATH_TOOL_KEY)
          // Сорванный запрос — это про нас, а не про кадр: обещать «в кадре
          // нет» на этом основании нельзя.
          setDrawn(true)
        })
    }, DEBOUNCE_MS)
    return () => {
      alive = false
      clearTimeout(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, step, viewKey])

  // Уход из раздела → снять линии, иначе повиснут над другой картой.
  useEffect(
    () => () => mapRef.current?.removeIsobaths(ISOBATH_TOOL_KEY),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return { drawn, pending: toolPending(loadedKey, `${step}|${viewKey}`, enabled) }
}
